import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { FiShield, FiAlertTriangle, FiSearch, FiX } from "react-icons/fi";
import dayjs from "dayjs";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const AdblockerLogs = () => {
  const token = localStorage.getItem("token");

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchQuery, setSearchQuery] = useState(() => {
    return new URLSearchParams(window.location.search).get("user_id") || "";
  });

  const fetchLogs = useCallback(async (page, query) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit: itemsPerPage });
      if (query) {
        if (/^\d+$/.test(query)) {
          params.set("user_id", query);
        } else {
          params.set("username", query);
        }
      }
      const res = await fetch(`${baseUrl}/adblocker/usage?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok && result.status) {
        setLogs(result.data.logs || []);
        setTotalLogs(result.data.total || 0);
      } else {
        setError(result.message || "Failed to fetch adblocker logs");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLogs(currentPage, searchQuery); }, [fetchLogs, currentPage, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLogs(1, searchQuery.trim());
  };

  const handleClear = () => {
    setSearchQuery("");
    setCurrentPage(1);
    fetchLogs(1, "");
  };

  const totalPages = Math.ceil(totalLogs / itemsPerPage);
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages || 1, startPage + maxVisiblePages - 1);
  if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          {/* Heading */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#ffffe0] flex items-center justify-center">
                <FiShield size={20} className="text-[#999900]" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">Adblocker Logs</h1>
                <p className="text-xs text-gray-500 mt-0.5">Track users who are using adblockers</p>
              </div>
            </div>

            <div className="bg-white border border-gray-100 shadow-sm rounded-xl px-4 py-1.5 text-center hidden sm:block">
              <p className="text-[10px] uppercase font-bold text-gray-400">Total Logs</p>
              <p className="text-lg font-black text-gray-800">{totalLogs.toLocaleString()}</p>
            </div>
          </div>

          {/* Filter bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by User ID or Username"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-sm font-medium px-4 py-2.5 rounded-lg transition"
            >
              <FiSearch size={14} /> Search
            </button>
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                <FiX size={14} /> Clear
              </button>
            )}
          </form>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">{error}</div>
          ) : logs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-24 text-gray-400">
              <FiAlertTriangle size={32} className="text-gray-300 mb-3" />
              <p className="text-sm">No adblocker logs found.</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                        <th className="px-5 py-3">ID</th>
                        <th className="px-5 py-3">User ID</th>
                        <th className="px-5 py-3">User Name</th>
                        <th className="px-5 py-3">Email</th>
                        <th className="px-5 py-3 text-right">Date/Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {logs.map((log) => {
                        return (
                          <tr key={log.id} className="hover:bg-gray-50 transition">
                            <td className="px-5 py-3.5 font-medium text-gray-800">
                              #{log.id}
                            </td>
                            <td className="px-5 py-3.5 text-gray-600">
                              {log.user_id}
                            </td>
                            <td className="px-5 py-3.5 font-medium text-gray-800">
                              {log.username || "-"}
                            </td>
                            <td className="px-5 py-3.5 text-gray-500">
                              {log.email}
                            </td>
                            <td className="px-5 py-3.5 text-right text-gray-500 text-xs">
                              {log.created_at ? dayjs(log.created_at).format("MMM D, YYYY HH:mm") : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg)}
                      className={`px-4 py-2 border rounded-lg text-sm transition ${
                        currentPage === pg ? "bg-[#ffff00] text-black border-[#ffff00]" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdblockerLogs;
